import {
  Container,
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import {
  collection,
  query,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  doc,
  where,
  updateDoc,
  onSnapshot,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../firebase';
import { Finance, TransactionCategory } from '../../types/Finance';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User } from '../../types/User';
import { Event } from '../../types/Event';
import { Timestamp } from 'firebase/firestore';
import { SubscriptionDialog } from '../../components/SubscriptionDialog';
import { useState, useEffect } from 'react';
import PageLayout from '../../components/PageLayout';
import PageTitle from '../../components/PageTitle';
import ResponsiveTitle from '../../components/ResponsiveTitle';
import { adminStyles } from '../../theme/theme';

const incomeCategories: { value: TransactionCategory; label: string }[] = [
  { value: 'subscription', label: 'Cotisation' },
  { value: 'donation', label: 'Donation' },
  { value: 'other_income', label: 'Autre revenu' },
];

const expenseCategories: { value: TransactionCategory; label: string }[] = [
  { value: 'equipment', label: 'Matériel' },
  { value: 'food', label: 'Nourriture' },
  { value: 'insurance', label: 'Assurance' },
  { value: 'bank', label: 'Banque' },
  { value: 'donation', label: 'Donation' },
  { value: 'other_expense', label: 'Autre dépense' },
];

interface Transaction extends Finance {
  id: string;
}

export const Finances = () => {
  const { user } = useAuth();
  const [selectedYears, setSelectedYears] = useState<number[]>([new Date().getFullYear()]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totals, setTotals] = useState({
    income: 0,
    expense: 0,
    balance: 0
  });

  const [users, setUsers] = useState<User[]>([]);
  const [events, setEvents] = useState<Event[]>([]);

  // États pour les détails de la cotisation
  const [subscriptionType, setSubscriptionType] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');

  useEffect(() => {
    const fetchUsers = async () => {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as User))
        .filter(user => user.firstName && user.lastName && user.lastName.toUpperCase() !== 'PARTI')
        .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
      setUsers(usersData);
    };

    const fetchEvents = async () => {
      const q = query(collection(db, 'events'), orderBy('startDate', 'desc'));
      const querySnapshot = await getDocs(q);
      const eventsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];
      setEvents(eventsData);
    };

    fetchUsers();
    fetchEvents();
  }, []);

  type Order = 'asc' | 'desc';
  interface SortConfig {
    key: keyof Transaction;
    direction: Order;
  }
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([{ key: 'date', direction: 'desc' }]);

  const handleRequestSort = (key: keyof Transaction, event: React.MouseEvent<unknown>) => {
    const isMultiSort = event.ctrlKey || event.metaKey;

    setSortConfig(prevConfig => {
      const existingIndex = prevConfig.findIndex(c => c.key === key);
      const existingConfig = prevConfig[existingIndex];

      let newConfig: SortConfig[];

      if (isMultiSort) {
        if (existingConfig) {
          // Toggle direction or remove if needed (here we just toggle)
          const updatedSort = { ...existingConfig, direction: existingConfig.direction === 'asc' ? 'desc' as Order : 'asc' as Order };
          newConfig = [...prevConfig];
          newConfig[existingIndex] = updatedSort;
        } else {
          // Add new sort key
          newConfig = [...prevConfig, { key, direction: 'asc' }];
        }
      } else {
        // Single sort mode
        if (existingConfig) {
          // Toggle direction if it's the only one or the primary one, otherwise reset to just this one
          const direction = existingConfig.direction === 'asc' ? 'desc' : 'asc';
          newConfig = [{ key, direction }];
        } else {
          newConfig = [{ key, direction: 'asc' }];
        }
      }
      return newConfig;
    });
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    for (const sort of sortConfig) {
      const { key, direction } = sort;

      // Gestion spécifique pour les dates
      if (key === 'date') {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        if (dateA !== dateB) {
          return direction === 'asc' ? dateA - dateB : dateB - dateA;
        }
      }

      // Gestion pour les montants
      else if (key === 'amount') {
        if (a.amount !== b.amount) {
          return direction === 'asc'
            ? a.amount - b.amount
            : b.amount - a.amount;
        }
      }

      // Gestion pour les chaînes de caractères (description, type, category)
      else {
        const valA = String(a[key] || '').toLowerCase();
        const valB = String(b[key] || '').toLowerCase();

        // Pour la catégorie, on essaie de trier par le libellé affiché plutôt que la valeur brute
        if (key === 'category') {
          const labelA = (a.type === 'income'
            ? incomeCategories.find(c => c.value === a.category)?.label
            : expenseCategories.find(c => c.value === a.category)?.label) || valA;
          const labelB = (b.type === 'income'
            ? incomeCategories.find(c => c.value === b.category)?.label
            : expenseCategories.find(c => c.value === b.category)?.label) || valB;

          if (labelA !== labelB) {
            return direction === 'asc'
              ? labelA.localeCompare(labelB)
              : labelB.localeCompare(labelA);
          }
        } else {
          if (valA !== valB) {
            return direction === 'asc'
              ? valA.localeCompare(valB)
              : valB.localeCompare(valA);
          }
        }
      }
    }
    return 0;
  });

  // États pour les menus
  const [incomeAnchor, setIncomeAnchor] = useState<null | HTMLElement>(null);
  const [expenseAnchor, setExpenseAnchor] = useState<null | HTMLElement>(null);

  // État pour le dialogue de nouvelle transaction
  const [openDialog, setOpenDialog] = useState(false);
  const [newTransaction, setNewTransaction] = useState<Partial<Transaction>>({
    type: 'expense',
    category: 'equipment',
    amount: 0,
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  // État pour le formulaire d'inventaire
  const [showInventoryFields, setShowInventoryFields] = useState(false);
  const [inventoryData, setInventoryData] = useState({
    name: '',
    quantity: 1,
    state: 'new',
    description: '',
    notes: ''
  });

  // État pour le dialogue de cotisation
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);

  // Réinitialiser les champs d'inventaire
  const resetInventoryData = () => {
    setInventoryData({
      name: '',
      quantity: 1,
      state: 'new',
      description: '',
      notes: ''
    });
    setShowInventoryFields(false);
  };

  const navigate = useNavigate();

  useEffect(() => {
    const yearData: { [year: number]: Transaction[] } = {};
    const unsubscribes: (() => void)[] = [];

    const updateTransactions = () => {
      const allTransactions = Object.values(yearData).flat();
      // Trier les transactions par date (plus récentes en premier) par défaut pour l'affichage brut
      // Le tri final est géré par sortedTransactions
      allTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setTransactions(allTransactions);

      // Calculer les totaux
      const calculatedTotals = allTransactions.reduce((acc, transaction) => {
        const amount = transaction.amount;
        if (transaction.type === 'income') {
          acc.income += amount;
          acc.balance += amount;
        } else {
          acc.expense += amount;
          acc.balance -= amount;
        }
        return acc;
      }, { income: 0, expense: 0, balance: 0 });

      setTotals(calculatedTotals);
    };

    selectedYears.forEach(year => {
      const startDate = new Date(year, 0, 1); // 1er janvier de l'année
      const endDate = new Date(year, 11, 31); // 31 décembre de l'année

      const q = query(
        collection(db, 'finances'),
        where('date', '>=', startDate.toISOString().split('T')[0]),
        where('date', '<=', endDate.toISOString().split('T')[0]),
        orderBy('date', 'desc')
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const transactionsForYear = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Transaction[];

        yearData[year] = transactionsForYear;
        updateTransactions();
      }, (error) => {
        console.error(`Error fetching transactions for year ${year}:`, error);
      });

      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [selectedYears]);

  // État pour le menu des années
  const [yearSelectOpen, setYearSelectOpen] = useState(false);

  useEffect(() => {
    // Afficher les champs d'inventaire si la catégorie est "equipment"
    if (newTransaction.category === 'equipment') {
      setShowInventoryFields(true);
    } else {
      setShowInventoryFields(false);
    }
  }, [newTransaction.category]);

  const handleIncomeClick = (event: React.MouseEvent<HTMLElement>) => {
    setIncomeAnchor(event.currentTarget);
  };

  const handleExpenseClick = (event: React.MouseEvent<HTMLElement>) => {
    setExpenseAnchor(event.currentTarget);
  };

  const handleCategoryClick = (type: 'income' | 'expense', category: TransactionCategory) => {
    if (type === 'income' && category === 'subscription') {
      // On force le passage par le formulaire de cotisation (SubscriptionDialog)
      setOpenSubscriptionDialog(true);
      setIncomeAnchor(null);
      setExpenseAnchor(null);
      return;
    }
    setNewTransaction({
      type,
      category,
      amount: 0,
      description: '',
      date: new Date().toISOString().split('T')[0],
      ...(category === 'equipment' ? { quantity: 1, unitPrice: 0 } : {})
    });
    setShowInventoryFields(category === 'equipment');
    setOpenDialog(true);
    setIncomeAnchor(null);
    setExpenseAnchor(null);
  };

  const handleCloseMenus = () => {
    setIncomeAnchor(null);
    setExpenseAnchor(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewTransaction({
      type: 'expense',
      category: 'equipment',
      amount: 0,
      description: '',
      date: new Date().toISOString().split('T')[0]
    });
    setSubscriptionType('');
    setPaymentMethod('');
    resetInventoryData();
  };

  const handleSubscriptionDialogClose = () => {
    setOpenSubscriptionDialog(false);
  };

  const handleSubmitTransaction = async () => {
    try {
      console.log('Submitting transaction...', newTransaction);

      if (!newTransaction.amount) {
        alert("Veuillez saisir un montant.");
        return;
      }

      if (!newTransaction.category) {
        alert("Catégorie manquante.");
        return;
      }

      // Si ce n'est PAS une cotisation et PAS du matériel (inventaire), la description est requise
      if (newTransaction.category !== 'subscription' && newTransaction.category !== 'equipment' && !newTransaction.description) {
        alert("Veuillez saisir une description.");
        return;
      }

      // 1. Préparation de la description
      let description = newTransaction.description;

      // Cas Matériel
      if (newTransaction.category === 'equipment' && showInventoryFields) {
        description = `${inventoryData.name} (${inventoryData.quantity} unité${inventoryData.quantity > 1 ? 's' : ''}) - ${inventoryData.description}`;
      }

      // 2. Initialisation de transactionData
      const transactionData: any = {
        ...newTransaction,
        description,
        createdAt: (newTransaction.id && newTransaction.createdAt) ? newTransaction.createdAt : new Date().toISOString(),
        createdBy: (newTransaction.id && newTransaction.createdBy) ? newTransaction.createdBy : (user?.uid || 'admin')
      };

      // Firestore n'accepte pas les valeurs undefined, on doit les supprimer
      Object.keys(transactionData).forEach(key => {
        if (transactionData[key] === undefined) {
          delete transactionData[key];
        }
      });

      // 3. Cas Cotisation
      if (newTransaction.category === 'subscription' && newTransaction.type === 'income') {
        const userId = newTransaction.userId;
        if (!userId) {
          alert("Veuillez sélectionner un adhérent.");
          return;
        }
        if (!subscriptionType) {
          alert("Veuillez sélectionner le type de cotisation.");
          return;
        }
        if (!paymentMethod) {
          alert("Veuillez sélectionner le mode de paiement.");
          return;
        }
        if (subscriptionType === 'event' && !newTransaction.eventId) {
          alert("Veuillez sélectionner un événement.");
          return;
        }

        const dateStr = newTransaction.date || new Date().toISOString().split('T')[0];
        const dateObj = new Date(dateStr);

        if (isNaN(dateObj.getTime())) {
          alert("Date invalide.");
          return;
        }

        // Créer l'objet cotisation
        const subscriptionData: any = {
          userId,
          type: subscriptionType,
          amount: Number(newTransaction.amount),
          paymentMethod,
          paymentDate: Timestamp.fromDate(dateObj),
          year: dateObj.getFullYear(),
          createdAt: Timestamp.now(),
          createdBy: user?.uid || 'admin'
        };

        if (subscriptionType === 'event' && newTransaction.eventId) {
          subscriptionData.eventId = newTransaction.eventId;
        }

        // Enregistrer la cotisation
        const subscriptionRef = await addDoc(collection(db, 'subscriptions'), subscriptionData);

        // Mettre à jour transactionData
        transactionData.subscriptionId = subscriptionRef.id;

        const selectedUser = users.find(u => u.id === userId);
        const userName = selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}${selectedUser.nickname ? ` (${selectedUser.nickname})` : ''}` : '';

        if (subscriptionType === 'annual') {
          transactionData.description = `Cotisation annuelle - ${userName}`;
        } else {
          const event = events.find(e => e.id === newTransaction.eventId);
          transactionData.description = `Cotisation événementielle "${event?.title}" - ${userName}`;
        }
      }

      // 4. Sauvegarde Transaction
      let transactionId = newTransaction.id;

      if (transactionId) {
        // Mise à jour d'une transaction existante
        await updateDoc(doc(db, 'finances', transactionId), transactionData);

        // Si c'est une dépense de matériel, mettre à jour l'inventaire
        if (newTransaction.category === 'equipment' && showInventoryFields) {
          const q = query(
            collection(db, 'inventory'),
            where('transactionId', '==', transactionId)
          );
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const inventoryDoc = querySnapshot.docs[0];
            const inventoryItem = {
              name: inventoryData.name,
              quantity: inventoryData.quantity,
              state: inventoryData.state,
              description: inventoryData.description,
              notes: inventoryData.notes,
              inventoryDate: newTransaction.date,
              updatedAt: new Date().toISOString()
            };
            await updateDoc(doc(db, 'inventory', inventoryDoc.id), inventoryItem);
          }
        }
      } else {
        // Création d'une nouvelle transaction
        const transactionDoc = await addDoc(collection(db, 'finances'), transactionData);
        transactionId = transactionDoc.id;

        // Si c'est une dépense de matériel, créer l'entrée dans l'inventaire
        if (newTransaction.category === 'equipment' && showInventoryFields) {
          const inventoryItem = {
            name: inventoryData.name,
            quantity: inventoryData.quantity,
            state: inventoryData.state,
            description: inventoryData.description,
            notes: inventoryData.notes,
            inventoryDate: newTransaction.date,
            transactionId: transactionId,
            createdAt: new Date().toISOString(),
            createdBy: user?.uid || ''
          };

          await addDoc(collection(db, 'inventory'), inventoryItem);
        }
      }

      // Recharger les transactions
      // NOTE: Le rechargement se fait via onSnapshot, pas besoin de le faire manuellement ici
      // mais on a besoin de fermer le dialog
      handleCloseDialog();
      resetInventoryData();
    } catch (error) {
      console.error('Error managing transaction:', error);
      alert(`Erreur lors de l'enregistrement : ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette transaction ?')) {
      return;
    }

    try {
      // On récupère la transaction pour savoir si c'est une cotisation
      const transactionDoc = await getDocs(query(collection(db, 'finances'), where('__name__', '==', id)));
      if (!transactionDoc.empty) {
        const transaction = transactionDoc.docs[0].data();
        // Si c'est une cotisation
        if (transaction.type === 'income' && transaction.category === 'subscription') {
          if (transaction.subscriptionId) {
            // Supprimer la cotisation
            await deleteDoc(doc(db, 'subscriptions', transaction.subscriptionId));
            // Supprimer toutes les transactions liées à cette cotisation
            const financesRef = collection(db, 'finances');
            const q = query(financesRef, where('subscriptionId', '==', transaction.subscriptionId));
            const querySnapshot = await getDocs(q);
            const batch = writeBatch(db);
            querySnapshot.forEach((docSnap) => {
              batch.delete(docSnap.ref);
            });
            await batch.commit();
            return;
          } else {
            alert("Impossible de supprimer la cotisation liée : l'identifiant de la cotisation (subscriptionId) est manquant dans la transaction.\nMerci de vérifier la cohérence des données dans Firestore.");
            console.error('Transaction de cotisation sans subscriptionId :', transaction);
            // Ici, on pourrait aussi supprimer la transaction, mais on préfère alerter pour éviter une incohérence.
            return;
          }
        }
      }
      // Si ce n'est pas une cotisation, on supprime simplement la transaction
      await deleteDoc(doc(db, 'finances', id));
      // La transaction sera automatiquement supprimée de l'état local grâce à onSnapshot
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      // Gérer l'erreur ici
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setNewTransaction(transaction);

    // Réinitialiser les états spécifiques
    setSubscriptionType('');
    setPaymentMethod('');

    if (transaction.category === 'equipment') {
      // Récupérer les données d'inventaire associées
      const fetchInventoryData = async () => {
        const q = query(
          collection(db, 'inventory'),
          where('transactionId', '==', transaction.id)
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const inventoryDoc = querySnapshot.docs[0];
          const data = inventoryDoc.data();
          setInventoryData({
            name: data.name,
            quantity: data.quantity,
            state: data.state,
            description: data.description,
            notes: data.notes || ''
          });
          setShowInventoryFields(true);
        }
      };
      fetchInventoryData();
    } else if (transaction.category === 'subscription' && transaction.subscriptionId) {
      // Récupérer les données de la cotisation associée
      const fetchSubscriptionData = async () => {
        try {
          const subDoc = await getDoc(doc(db, 'subscriptions', transaction.subscriptionId!));
          if (subDoc.exists()) {
            const subData = subDoc.data();
            setSubscriptionType(subData.type || '');
            setPaymentMethod(subData.paymentMethod || '');

            // Si c'est une cotisation événementielle, s'assurer que l'eventId est bien défini
            if (subData.type === 'event' && subData.eventId) {
              setNewTransaction(prev => ({ ...prev, eventId: subData.eventId }));
            }
          }
        } catch (error) {
          console.error("Erreur lors de la récupération de la cotisation:", error);
        }
      };
      fetchSubscriptionData();
    }
    setOpenDialog(true);
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  return (
    <PageLayout>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 2, md: 0 },
        mb: 3
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: 4,
          px: 3,
          py: 1,
          color: 'white',
          ml: { xs: 9, md: 0 },
          width: { xs: 'calc(100% - 90px)', md: 'auto' },
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <AccountBalanceIcon />
          <ResponsiveTitle variant="h4">
            Gestion des Finances
          </ResponsiveTitle>
        </Box>

        <Box sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          width: { xs: '100%', md: 'auto' }
        }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 120,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: 1,
              '& .MuiInputBase-root': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'white' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' }
            }}
          >
            <InputLabel id="year-select-label">Année</InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYears}
              label="Année"
              multiple
              open={yearSelectOpen}
              onOpen={() => setYearSelectOpen(true)}
              onClose={() => setYearSelectOpen(false)}
              renderValue={(selected) => (selected as number[]).sort((a, b) => b - a).join(', ')}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <MenuItem
                  key={year}
                  value={year}
                  onClick={(e) => {
                    const isMulti = e.ctrlKey || e.metaKey;
                    if (isMulti) {
                      e.stopPropagation(); // Empêcher le menu de se fermer
                      if (selectedYears.includes(year)) {
                        // On interdit de désélectionner la dernière année pour éviter une liste vide
                        if (selectedYears.length > 1) {
                          setSelectedYears(selectedYears.filter(y => y !== year));
                        }
                      } else {
                        setSelectedYears([...selectedYears, year]);
                      }
                    } else {
                      setSelectedYears([year]);
                      // Le menu se fermera automatiquement car on n'a pas appelé stopPropagation
                      setYearSelectOpen(false); // On force la fermeture pour être sûr
                    }
                  }}
                  sx={{
                    fontWeight: selectedYears.includes(year) ? 'bold' : 'normal',
                    // Ajouter une couleur de fond si sélectionné pour visibilité
                    bgcolor: selectedYears.includes(year) ? 'action.selected' : 'transparent',
                    '&:hover': {
                      bgcolor: selectedYears.includes(year) ? 'action.selected' : 'action.hover',
                    }
                  }}
                >
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ButtonGroup>
            <Button
              variant="contained"
              onClick={handleIncomeClick}
              startIcon={<TrendingUpIcon />}
              color="primary"
            >
              Revenu
            </Button>
            <Button
              variant="contained"
              onClick={handleExpenseClick}
              startIcon={<TrendingDownIcon />}
              color="error"
            >
              Dépense
            </Button>
          </ButtonGroup>

          <Menu
            anchorEl={incomeAnchor}
            open={Boolean(incomeAnchor)}
            onClose={handleCloseMenus}
          >
            {incomeCategories.map((category) => (
              <MenuItem
                key={category.value}
                onClick={() => handleCategoryClick('income', category.value)}
              >
                {category.label}
              </MenuItem>
            ))}
          </Menu>

          <Menu
            anchorEl={expenseAnchor}
            open={Boolean(expenseAnchor)}
            onClose={handleCloseMenus}
          >
            {expenseCategories.map((category) => (
              <MenuItem
                key={category.value}
                onClick={() => handleCategoryClick('expense', category.value)}
              >
                {category.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        mb: 4
      }}>
        <Card sx={{ flex: 1, bgcolor: '#1976d2' }}>
          <CardContent>
            <Typography color="white" gutterBottom>
              Revenus Totaux
            </Typography>
            <Typography variant="h5" component="div" color="white">
              {formatAmount(totals.income)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, bgcolor: '#d32f2f' }}>
          <CardContent>
            <Typography color="white" gutterBottom>
              Dépenses Totales
            </Typography>
            <Typography variant="h5" component="div" color="white">
              {formatAmount(totals.expense)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, bgcolor: totals.balance >= 0 ? '#2e7d32' : '#d32f2f' }}>
          <CardContent>
            <Typography color="white" gutterBottom>
              Solde
            </Typography>
            <Typography variant="h5" component="div" color="white">
              {formatAmount(totals.balance)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.some(c => c.key === 'date')}
                  direction={sortConfig.find(c => c.key === 'date')?.direction || 'asc'}
                  onClick={(event) => handleRequestSort('date', event)}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.some(c => c.key === 'type')}
                  direction={sortConfig.find(c => c.key === 'type')?.direction || 'asc'}
                  onClick={(event) => handleRequestSort('type', event)}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.some(c => c.key === 'category')}
                  direction={sortConfig.find(c => c.key === 'category')?.direction || 'asc'}
                  onClick={(event) => handleRequestSort('category', event)}
                >
                  Catégorie
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.some(c => c.key === 'description')}
                  direction={sortConfig.find(c => c.key === 'description')?.direction || 'asc'}
                  onClick={(event) => handleRequestSort('description', event)}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortConfig.some(c => c.key === 'amount')}
                  direction={sortConfig.find(c => c.key === 'amount')?.direction || 'asc'}
                  onClick={(event) => handleRequestSort('amount', event)}
                >
                  Montant
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTransactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Aucune transaction pour le moment
                </TableCell>
              </TableRow>
            ) : (
              sortedTransactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>{new Date(transaction.date).toLocaleDateString('fr-FR')}</TableCell>
                  <TableCell>{transaction.type === 'income' ? 'Revenu' : 'Dépense'}</TableCell>
                  <TableCell>
                    {transaction.type === 'income'
                      ? incomeCategories.find(c => c.value === transaction.category)?.label
                      : expenseCategories.find(c => c.value === transaction.category)?.label}
                  </TableCell>
                  <TableCell>{transaction.description}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: transaction.type === 'income' ? '#2e7d32' : '#d32f2f',
                      fontWeight: 'bold'
                    }}
                  >
                    {formatAmount(transaction.amount)}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        onClick={() => handleEdit(transaction)}
                        sx={{
                          ...adminStyles.glassButton,
                          '&:hover': {
                            ...adminStyles.glassButton['&:hover'],
                            bgcolor: 'primary.main'
                          }
                        }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => transaction.id && handleDelete(transaction.id)}
                        sx={{
                          ...adminStyles.glassButton,
                          '&:hover': {
                            ...adminStyles.glassButton['&:hover'],
                            bgcolor: 'error.main'
                          }
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {newTransaction.type === 'income' ? 'Nouveau revenu' : 'Nouvelle dépense'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newTransaction.type}
                  label="Type"
                  onChange={(e) => {
                    const newType = e.target.value as 'income' | 'expense';
                    const newCategories = newType === 'income' ? incomeCategories : expenseCategories;
                    setNewTransaction({
                      ...newTransaction,
                      type: newType,
                      category: newCategories[0].value
                    });
                  }}
                >
                  <MenuItem value="income">Revenu</MenuItem>
                  <MenuItem value="expense">Dépense</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={newTransaction.category}
                  label="Catégorie"
                  onChange={(e) => {
                    setNewTransaction({
                      ...newTransaction,
                      category: e.target.value as TransactionCategory
                    });
                  }}
                >
                  {(newTransaction.type === 'income' ? incomeCategories : expenseCategories).map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {newTransaction.category === 'subscription' && newTransaction.type === 'income' ? (
              <>
                <FormControl fullWidth required>
                  <InputLabel>Adhérent</InputLabel>
                  <Select
                    value={newTransaction.userId || ''}
                    label="Adhérent"
                    onChange={(e) => {
                      const userId = e.target.value;
                      setNewTransaction({ ...newTransaction, userId });
                    }}
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} {user.nickname ? `(${user.nickname})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Type de cotisation</InputLabel>
                    <Select
                      value={subscriptionType}
                      label="Type de cotisation"
                      onChange={(e) => {
                        setSubscriptionType(e.target.value);
                        if (e.target.value !== 'event') {
                          setNewTransaction(prev => ({ ...prev, eventId: undefined }));
                        }
                      }}
                    >
                      <MenuItem value="annual">Annuelle</MenuItem>
                      <MenuItem value="event">Événementielle</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth required>
                    <InputLabel>Mode de paiement</InputLabel>
                    <Select
                      value={paymentMethod}
                      label="Mode de paiement"
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <MenuItem value="especes">Espèces</MenuItem>
                      <MenuItem value="virement">Virement</MenuItem>
                      <MenuItem value="cheque">Chèque</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {subscriptionType === 'event' && (
                  <FormControl fullWidth required>
                    <InputLabel>Événement</InputLabel>
                    <Select
                      value={newTransaction.eventId || ''}
                      label="Événement"
                      onChange={(e) => setNewTransaction({ ...newTransaction, eventId: e.target.value })}
                    >
                      {events.map((event) => (
                        <MenuItem key={event.id} value={event.id}>
                          {event.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            ) : (
              !showInventoryFields && (
                <TextField
                  label="Description"
                  value={newTransaction.description}
                  onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                  fullWidth
                  required
                  multiline
                  rows={2}
                />
              )
            )}

            <TextField
              label="Date"
              type="date"
              value={newTransaction.date}
              onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Montant"
              type="number"
              value={newTransaction.amount || ''}
              onChange={(e) => setNewTransaction({ ...newTransaction, amount: parseFloat(e.target.value) || 0 })}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
            />

            {/* Champs supplémentaires pour l'inventaire si c'est une dépense de matériel */}
            {showInventoryFields ? (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Informations pour l'inventaire
                </Typography>

                <TextField
                  label="Nom du matériel"
                  value={inventoryData.name}
                  onChange={(e) => {
                    setInventoryData({ ...inventoryData, name: e.target.value });
                    setNewTransaction({
                      ...newTransaction,
                      description: `${e.target.value}${inventoryData.description ? ` - ${inventoryData.description}` : ''}`
                    });
                  }}
                  fullWidth
                  required
                />

                <TextField
                  label="Quantité"
                  type="number"
                  value={inventoryData.quantity}
                  onChange={(e) => setInventoryData({ ...inventoryData, quantity: Number(e.target.value) })}
                  fullWidth
                  required
                  InputProps={{ inputProps: { min: 1 } }}
                />

                <TextField
                  select
                  label="État"
                  value={inventoryData.state}
                  onChange={(e) => setInventoryData({ ...inventoryData, state: e.target.value })}
                  fullWidth
                  required
                >
                  <MenuItem value="new">Neuf</MenuItem>
                  <MenuItem value="good">Bon état</MenuItem>
                  <MenuItem value="used">Usagé</MenuItem>
                  <MenuItem value="damaged">Endommagé</MenuItem>
                </TextField>

                <TextField
                  label="Description détaillée"
                  value={inventoryData.description}
                  onChange={(e) => {
                    setInventoryData({ ...inventoryData, description: e.target.value });
                    setNewTransaction({
                      ...newTransaction,
                      description: `${inventoryData.name}${e.target.value ? ` - ${e.target.value}` : ''}`
                    });
                  }}
                  fullWidth
                  multiline
                  rows={2}
                />

                <TextField
                  label="Notes supplémentaires"
                  value={inventoryData.notes}
                  onChange={(e) => setInventoryData({ ...inventoryData, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </>
            ) : null}

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmitTransaction} variant="contained" color="primary">
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      <SubscriptionDialog
        open={openSubscriptionDialog}
        onClose={handleSubscriptionDialogClose}
        users={users}
      />
    </PageLayout>
  );
};
